import { Box, Text, VStack } from '@chakra-ui/react';
import i18n from 'i18next';
import React from 'react';
import { useTranslation } from 'react-i18next';

import ScrollableTable from './ScrollableTable';
interface UsageHistoryDetailTabProps {
  usageData: any;
}

const UsageHistoryDetailTab: React.FC<UsageHistoryDetailTabProps> = ({ usageData }) => {
  const { t } = useTranslation();

  const colorText = 'text.default';

  const formatDate = (yearMonth: any) => {
    if (!yearMonth) {
      return 'N/A';
    }

    // Handle string format like "2025-08"
    if (typeof yearMonth === 'string') {
      const [year, month] = yearMonth.split('-').map(Number);
      const date = new Date(year, month - 1);
      return date.toLocaleDateString(i18n.language, {
        year: 'numeric',
        month: 'short',
      });
    }

    // Handle object format like { year: 2025, month: "AUGUST" }
    if (yearMonth.year && yearMonth.month) {
      const monthMap: any = {
        JANUARY: 0,
        FEBRUARY: 1,
        MARCH: 2,
        APRIL: 3,
        MAY: 4,
        JUNE: 5,
        JULY: 6,
        AUGUST: 7,
        SEPTEMBER: 8,
        OCTOBER: 9,
        NOVEMBER: 10,
        DECEMBER: 11,
      };
      const date = new Date(yearMonth.year, monthMap[yearMonth.month]);
      return date.toLocaleDateString(i18n.language, {
        year: 'numeric',
        month: 'short',
      });
    }

    return 'N/A';
  };

  // Sort newest-first on RAW year_month (YYYY*100+MM), not localized formatDate output.
  const sortKey = (ym: any): number => {
    if (!ym) return 0;
    if (typeof ym === 'string') {
      const [y, m] = ym.split('-').map(Number);
      return (y || 0) * 100 + (m || 0);
    }
    if (ym.year && ym.month) {
      const M: any = {
        JANUARY: 1,
        FEBRUARY: 2,
        MARCH: 3,
        APRIL: 4,
        MAY: 5,
        JUNE: 6,
        JULY: 7,
        AUGUST: 8,
        SEPTEMBER: 9,
        OCTOBER: 10,
        NOVEMBER: 11,
        DECEMBER: 12,
      };
      return ym.year * 100 + (M[ym.month] || 0);
    }
    return 0;
  };
  const sortedData = React.useMemo(() => {
    const data = usageData?.monthly_details || [];
    return [...data].sort((a, b) => sortKey(b.year_month) - sortKey(a.year_month));
  }, [usageData]);

  return (
    <VStack align='stretch' h='90%' spacing={4}>
      <Box overflow='hidden' h='400px' borderRadius='lg'>
        <ScrollableTable
          columns={[
            {
              title: t('managementSystem.companyDetail.date'),
              dataIndex: 'year_month',
              key: 'year_month',
              width: '120px',
              render: (row: any) => <Text color={colorText}>{formatDate(row.year_month)}</Text>,
            },
            {
              title: t('managementSystem.companyDetail.email'),
              dataIndex: 'user_email',
              key: 'user_email',
              width: '180px',
              render: (row: any) => <Text color={colorText}>{row.user_email}</Text>,
            },
            {
              title: t('managementSystem.companyDetail.questions'),
              dataIndex: 'total_questions',
              key: 'total_questions',
              width: '120px',
              align: 'right',
              render: (row: any) => <Text color={colorText}>{row.total_questions}</Text>,
            },
            {
              title: t('managementSystem.companyDetail.uploadedData'),
              dataIndex: 'total_storage_gb',
              key: 'total_storage_gb',
              width: '160px',
              align: 'right',
              render: (row: any) => (
                <Text color={colorText}>
                  {typeof row.total_storage_gb === 'number'
                    ? (() => {
                        const storageInMB = row.total_storage_gb * 1024;
                        return storageInMB % 1 === 0
                          ? `${storageInMB}`
                          : `${storageInMB.toFixed(2).replace(/\.?0+$/, '')}`;
                      })()
                    : row.total_storage_gb}
                </Text>
              ),
            },
            {
              title: t('managementSystem.companyDetail.translations'),
              dataIndex: 'total_translations',
              key: 'total_translations',
              width: '120px',
              align: 'right',
              render: (row: any) => <Text color={colorText}>{row.total_translations}</Text>,
            },
          ]}
          data={sortedData}
          rowKey={(row) => `${row.user_email}-${row.year_month}`}
          noDataText={t('managementSystem.companyDetail.noData')}
        />
      </Box>
    </VStack>
  );
};

export default UsageHistoryDetailTab;
